import { useTranslation } from 'react-i18next'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  RISK_AVOIDANCE_VALUES,
  RISK_FREQUENCY_VALUES,
  RISK_INJURY_VALUES,
  RISK_LIKELIHOOD_VALUES,
} from './constants/riskAssessmentEnums'
import {
  getRiskDimensionLabel,
  isRiskOutHigherThanRiskIn,
  isRiskOutOptionDisabled,
  type RiskDimensionKey,
} from './riskOutDimensionCap'

export type RiskDimensionSelectProps = {
  value: string
  onValueChange: (value: string) => void
  placeholder: string
  id?: string
  className?: string
  disabled?: boolean
  maxAllowedValue?: string | null
}

function handleRiskDimensionValueChange(
  onValueChange: (value: string) => void,
  value: string | null
) {
  if (value) {
    onValueChange(value)
  }
}

type RiskDimensionSelectInternalProps = RiskDimensionSelectProps & {
  dimension: RiskDimensionKey
  options: readonly { value: string; label: string }[]
  enumKeyPrefix:
    | 'hazardsPage.enums.riskInjury'
    | 'hazardsPage.enums.riskFrequency'
    | 'hazardsPage.enums.riskAvoidance'
    | 'hazardsPage.enums.riskLikelihood'
}

function RiskDimensionSelectInternal({
  value,
  onValueChange,
  placeholder,
  id,
  className = 'w-full',
  disabled = false,
  maxAllowedValue,
  dimension,
  options,
  enumKeyPrefix,
}: RiskDimensionSelectInternalProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')

  const getOptionLabel = (optionValue: string, fallbackLabel: string) =>
    t(`${enumKeyPrefix}.${optionValue}`, fallbackLabel)

  const maxAllowedLabel =
    maxAllowedValue && maxAllowedValue.trim() !== ''
      ? getOptionLabel(
          maxAllowedValue,
          getRiskDimensionLabel(dimension, maxAllowedValue)
        )
      : null

  const selectedValueAboveCap =
    value.trim() !== '' &&
    isRiskOutHigherThanRiskIn(dimension, value, maxAllowedValue)

  let riskOutAboveCapHelperText: string | null = null
  if (maxAllowedLabel) {
    riskOutAboveCapHelperText = t(
      'hazardsPage.hazardDetail.riskFlow.riskOutOptionHigherThanRiskIn',
      { riskIn: maxAllowedLabel }
    )
  }

  const showSelectedAboveCapMessage =
    selectedValueAboveCap && riskOutAboveCapHelperText != null

  return (
    <div className='flex w-full flex-col gap-1.5'>
    <Select
      value={value ? value : null}
      onValueChange={(nextValue) =>
        handleRiskDimensionValueChange(onValueChange, nextValue)
      }
      disabled={disabled}
      items={options.map((option) => {
        const label = getOptionLabel(option.value, option.label)
        const optionDisabled = isRiskOutOptionDisabled(
          dimension,
          option.value,
          maxAllowedValue
        )
        return {
          value: option.value,
          label,
          disabled: optionDisabled,
        }
      })}
    >
      <SelectTrigger
        id={id}
        className={className}
        aria-invalid={selectedValueAboveCap}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => {
          const label = getOptionLabel(option.value, option.label)
          const optionDisabled = isRiskOutOptionDisabled(
            dimension,
            option.value,
            maxAllowedValue
          )

          return (
            <SelectItem
              key={option.value}
              value={option.value}
              disabled={optionDisabled}
              className={optionDisabled ? 'items-start py-2' : undefined}
            >
              <span className='flex flex-col gap-0.5'>
                <span>{label}</span>
                {optionDisabled && riskOutAboveCapHelperText ? (
                  <span className='text-xs text-muted-foreground'>
                    {riskOutAboveCapHelperText}
                  </span>
                ) : null}
              </span>
            </SelectItem>
          )
        })}
      </SelectContent>
    </Select>
    {showSelectedAboveCapMessage && (
      <p className='text-sm text-destructive' role='alert'>
        {riskOutAboveCapHelperText}
      </p>
    )}
    </div>
  )
}

export function RiskInjurySelect(props: RiskDimensionSelectProps) {
  return (
    <RiskDimensionSelectInternal
      {...props}
      dimension='injury'
      options={RISK_INJURY_VALUES}
      enumKeyPrefix='hazardsPage.enums.riskInjury'
    />
  )
}

export function RiskFrequencySelect(props: RiskDimensionSelectProps) {
  return (
    <RiskDimensionSelectInternal
      {...props}
      dimension='frequency'
      options={RISK_FREQUENCY_VALUES}
      enumKeyPrefix='hazardsPage.enums.riskFrequency'
    />
  )
}

export function RiskAvoidanceSelect(props: RiskDimensionSelectProps) {
  return (
    <RiskDimensionSelectInternal
      {...props}
      dimension='avoidance'
      options={RISK_AVOIDANCE_VALUES}
      enumKeyPrefix='hazardsPage.enums.riskAvoidance'
    />
  )
}

export function RiskLikelihoodSelect(props: RiskDimensionSelectProps) {
  return (
    <RiskDimensionSelectInternal
      {...props}
      dimension='likelihood'
      options={RISK_LIKELIHOOD_VALUES}
      enumKeyPrefix='hazardsPage.enums.riskLikelihood'
    />
  )
}
